import React, { memo } from 'react';
import cn from 'clsx';
import { Checkbox } from '@/components/ui/checkbox';
import { Avatar } from 'r/ui';
import { useNavigate, useLocation } from 'react-router-dom';
import type { AnalysisData } from './types';

const AdminHistoryRow = memo(
  ({
    analysis,
    isSelected,
    onToggleSelect,
  }: {
    analysis: AnalysisData;
    isSelected: boolean;
    onToggleSelect: (analysis: AnalysisData) => void;
  }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const isAlreadyVerified = !!analysis.verifiedBy;

    const handleCheckboxChange = () => {
      onToggleSelect(analysis);
    };

    const handleCheckboxClick = (e: React.MouseEvent) => {
      e.stopPropagation();
    };

    return (
      <div
        className={cn(
          'border border-text-tertiary rounded-lg p-4 hover:bg-accent transition-colors cursor-pointer relative',
          'bg-card',
          isSelected && 'ring-2 ring-(--focus) ring-inset',
          isAlreadyVerified && 'border-l-4 border-l-yellow-500',
        )}
        onClick={() =>
          navigate(`/admin-history/${analysis.id}/edit`, {
            state: { background: location },
          })
        }
      >
        <div
          className="absolute top-3 right-3 z-20"
          onClick={handleCheckboxClick}
        >
          <Checkbox
            checked={isSelected}
            onCheckedChange={handleCheckboxChange}
            aria-label={`Select analysis by ${analysis.tweetData.name}`}
            className={cn('w-5 h-5', isAlreadyVerified && 'border-yellow-500')}
          />
        </div>

        <a
          className={cn(
            'absolute top-3 left-1/2 transform -translate-x-1/2 h-8 text-sm font-normal underline cursor-pointer',
            analysis.tweetData.isDeletedFromTweeter ?
              'text-(--bearish)'
            : 'text-focus',
          )}
          href={`https://x.com/${analysis.tweetData.id}/status/${analysis.tweetData.ID}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          Original{' '}
          {analysis.tweetData.isDeletedFromTweeter ? 'deleted' : 'tweet'} link
        </a>

        <div className="flex justify-between items-start mb-2">
          <div className="flex items-center space-x-2">
            <Avatar
              size={24}
              user={{
                id: analysis.tweetData.name,
                avatar: analysis.tweetData.icon,
              }}
            />
            <span className="font-semibold text-main">
              {analysis.tweetData.name}
            </span>
            <span className="text-secondary text-sm">
              @{analysis.tweetData.id}
            </span>
            {isAlreadyVerified && (
              <span className="bg-yellow-500 text-main text-xs px-2 py-0.5 rounded">
                Verified
              </span>
            )}
          </div>
          <span className="pr-8 -mt-1.5 text-secondary text-sm flex flex-col">
            Tweet date: {new Date(analysis.openDate).toLocaleDateString()}
            <span className="text-xs">
              Saved: {new Date(analysis.savedAt).toLocaleDateString()}
            </span>
          </span>
        </div>

        <div className="text-main text-sm mb-2 line-clamp-2 pr-8">
          {analysis.tweetData.text}
        </div>

        <div className="flex justify-between items-center text-sm">
          <div className="flex items-center space-x-4">
            <span className="text-secondary">Pair: {analysis.pair.symbol}</span>
            <span
              className={cn(
                'font-semibold',
                analysis.isPredictionUp ? 'text-(--bullish)' : (
                  'text-(--bearish)'
                ),
              )}
            >
              {analysis.isPredictionUp ? 'UP' : 'DOWN'}
            </span>
            {analysis.performances && (
              <span className="flex items-center gap-2 text-xs">
                {(
                  [
                    ['1d', analysis.performances.performance_1d],
                    ['1w', analysis.performances.performance_1w],
                    ['2w', analysis.performances.performance_2w],
                    ['1M', analysis.performances.performance_1M],
                  ] as const
                ).map(([label, raw]) => {
                  if (raw == null) return null;
                  const val = parseFloat(raw);
                  if (isNaN(val)) return null;
                  return (
                    <span
                      key={label}
                      className={cn(
                        'font-medium',
                        val >= 0 ? 'text-(--bullish)' : 'text-(--bearish)',
                      )}
                    >
                      <span className="text-main">{label}:</span>{' '}
                      {(val * 100).toFixed(1)}%
                    </span>
                  );
                })}
              </span>
            )}
          </div>
          {isAlreadyVerified && (
            <span className="text-yellow-600 text-xs truncate max-w-30">
              by {analysis.verifiedBy}
            </span>
          )}
        </div>
      </div>
    );
  },
);

export default AdminHistoryRow;
